import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Crown, Loader2, CheckCircle2, XCircle, AlertTriangle, Info,
  ChevronDown, Sparkles, History, FileText,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import CompanyPdfUpload from '../components/CompanyPdfUpload';
import { companyPdfErrorKey, uploadCompanyPdf } from '../api/companyPdf';
import { formatFinancialValue } from '../utils/financialInput';

import Pagination from '../components/Pagination';
import PfuResultCard, { PfuResult as CalcResult } from '../components/PfuResultCard';

/* ─── Types ──────────────────────────────────────────────── */
interface Category { key: string; name: string; }

interface VipResult {
  category_name: string; mrp_value: number;
  max_contract_amount: number; max_contract_mrp: number;
  best_tier_max_mrp: number | null; pun_warning: string | null;
}

interface HistoryItem {
  id: number;
  created_at: string;
  category_key: string;
  category_name?: string;
  purchase_amount: number | null;
  is_vip: boolean;
  result: any;
}

/* ─── Helpers ─────────────────────────────────────────────── */
const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(Math.round(n));

// Format an amount string: group thousands with spaces, keep up to 2 decimal digits.
// Accepts digits, spaces (thousands separator), period or comma as decimal.
const fmtInput = (raw: string): string => {
  // Normalise: replace commas with dots, strip everything except digits and one dot
  let s = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
  // Keep only the first decimal point
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  // Limit fractional part to 2 digits
  const [intPart, fracPart] = s.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0'); // non-breaking space
  if (fracPart !== undefined) return formattedInt + '.' + fracPart.slice(0, 2);
  return formattedInt;
};

const parseNum = (s: string) => parseFloat(s.replace(/[\u00a0\s]/g, '').replace(',', '.')) || 0;

// The existing API stores the PUN warning as Russian prose, including in old
// history. Preserve its two numeric values while presenting localized score
// wording rather than repeating the former admission verdict.
const localizedPunWarning = (warning: string, t: (key: string) => string) => {
  const values = warning.match(/\(([-+]?\d+(?:[.,]\d+)?)\)/g);
  return values && values.length >= 2
    ? t('calc.vipPunExplanation').replace('{actual}', values[0].slice(1, -1)).replace('{target}', values[1].slice(1, -1))
    : t('calc.vipPunExplanationFallback');
};

/* ─── Component ───────────────────────────────────────────── */
export default function Calculator() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [activeTab, setActiveTab] = useState<'analysis' | 'history'>('analysis');

  // Categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  // Form
  const [selectedCategory, setSelectedCategory] = useState('');
  const [income, setIncome] = useState('');
  const [taxes, setTaxes] = useState('');
  const [pfot, setPfot] = useState('');
  const [amount, setAmount] = useState('');
  const [companyApplied, setCompanyApplied] = useState(false);

  // VIP mode
  const [vipMode, setVipMode] = useState(() => searchParams.get('mode') === 'vip');

  // Results
  const [calcResult, setCalcResult] = useState<CalcResult | null>(null);
  const [vipResult, setVipResult] = useState<VipResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcError, setCalcError] = useState('');

  // History
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistory, setSelectedHistory] = useState<HistoryItem | null>(null);

  // Company modal
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalIncome, setModalIncome] = useState('');
  const [modalTaxes, setModalTaxes] = useState('');
  const [modalPfot, setModalPfot] = useState('');
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [showPdf, setShowPdf] = useState(false);

  /* ─── Effects ─────────────────────────────────────────── */
  useEffect(() => {
    setVipMode(searchParams.get('mode') === 'vip');
    setActiveTab('analysis'); setCalcResult(null); setVipResult(null); setCalcError('');
  }, [searchParams]);
  useEffect(() => {
    api.get('/pfu/categories')
      .then(res => setCategories(res.data))
      .catch(() => {})
      .finally(() => setCategoriesLoading(false));
  }, []);

  const fetchHistory = useCallback(async (page: number) => {
    setIsHistoryLoading(true); setHistoryError('');
    try {
      const res = await api.get('/users/me/pfu-history', { params: { page, limit: 10 } });
      const data = res.data;
      setHistoryItems(data.items ?? data);
      setHistoryTotalPages(data.total_pages ?? 1);
      setHistoryPage(data.page ?? page);
    } catch { setHistoryError(t('calc.errorDefault')); }
    finally { setIsHistoryLoading(false); }
  }, [t]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(1);
  }, [activeTab, fetchHistory]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Handlers ────────────────────────────────────────── */
  const applyCompanyData = () => {
    if (!user?.company_profile) { setShowModal(true); return; }
    const cp = user.company_profile;
    setIncome(formatFinancialValue(cp.income_3yrs));
    setTaxes(formatFinancialValue(cp.taxes_3yrs));
    setPfot(formatFinancialValue(cp.pfot_3yrs));
    setCompanyApplied(true);
  };

  const handlePdfFile = async (file: File) => {
    if (isPdfUploading || isSavingModal || isCalculating) return;
    if (!file.name.toLowerCase().endsWith('.pdf') || file.size > 5 * 1024 * 1024) {
      setPdfStatus({ type: 'error', msg: t('calc.pdfError400') }); return;
    }
    setIsPdfUploading(true); setPdfStatus(null);
    try {
      const data = await uploadCompanyPdf(file);
      const { income_3yrs, taxes_3yrs, pfot_3yrs } = data;
      const inc = formatFinancialValue(income_3yrs);
      const tax = formatFinancialValue(taxes_3yrs);
      const pft = formatFinancialValue(pfot_3yrs);
      setModalIncome(inc); setModalTaxes(tax); setModalPfot(pft);
      setIncome(inc); setTaxes(tax); setPfot(pft); setCompanyApplied(true);
      setPdfStatus({ type: 'success', msg: t('calc.pdfSuccess') });
      await refreshUser();
      setTimeout(() => setShowModal(false), 1500);
    } catch (err: any) {
      setPdfStatus({ type: 'error', msg: t(companyPdfErrorKey(err, 'calc')) });
    } finally { setIsPdfUploading(false); }
  };

  const saveModalData = async () => {
    setIsSavingModal(true);
    try {
      await api.put('/users/me', {
        company_profile: { income_3yrs: parseNum(modalIncome), taxes_3yrs: parseNum(modalTaxes), pfot_3yrs: parseNum(modalPfot) },
      });
      setIncome(modalIncome); setTaxes(modalTaxes); setPfot(modalPfot); setCompanyApplied(true);
      setShowModal(false); await refreshUser();
    } catch { } finally { setIsSavingModal(false); }
  };

  const calculate = async () => {
    setIsCalculating(true); setCalcError(''); setCalcResult(null); setVipResult(null);
    try {
      if (vipMode) {
        const res = await api.post('/pfu/max-accessible-contract', {
          category_key: selectedCategory,
          income_3yrs: parseNum(income),
          taxes_3yrs: parseNum(taxes),
          pfot_3yrs: parseNum(pfot),
        });
        setVipResult(res.data);
      } else {
        const res = await api.post('/pfu/calculate', {
          category_key: selectedCategory,
          income_3yrs: parseNum(income),
          taxes_3yrs: parseNum(taxes),
          pfot_3yrs: parseNum(pfot),
          purchase_amount: parseNum(amount),
        });
        setCalcResult(res.data);
      }
    } catch (err: any) {
      const s = err.response?.status;
      setCalcError(s === 402 ? t('calc.error402') : s === 404 ? t('calc.error404') : s === 422 ? t('calc.error422') : s === 503 ? t('calc.error503') : t('calc.errorDefault'));
    } finally { setIsCalculating(false); }
    await refreshUser();
  };

  const selectedCategoryName = categories.find(c => c.key === selectedCategory)?.name ?? '';
  const limitsLeft = vipMode ? (user?.balance?.vip_calculations_left ?? 1) : (user?.balance?.pfu_calculations_left ?? 1);
  const canCalculate = !!(selectedCategory && parseNum(income) > 0 && (vipMode || parseNum(amount) > 0) && limitsLeft > 0);

  /* ─── Render ──────────────────────────────────────────── */
  return (
    <div className="workspace-page workspace-form-width">
        <header className="workspace-header"><div>
          <h1 className="workspace-title">{t('nav.calculator')}</h1>
          <p className="workspace-subtitle">{language === 'kk' ? 'Таңдалған сатып алу сомасы үшін компанияның ПФУ балын есептеңіз.' : 'Рассчитайте балл ПФУ компании для выбранной суммы закупки.'}</p>
        </div></header>
        <div className="space-y-6">

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-100 shadow-sm">
          {(['analysis', 'history'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all ${activeTab === tab ? 'bg-goszakup-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'analysis' ? t('calc.tabAnalysis') : t('calc.tabHistory')}
            </button>
          ))}
        </div>

        {/* ── ANALYSIS TAB ── */}
        {activeTab === 'analysis' && (
          <>
            {/* Company data */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                <h2 className="font-semibold text-gray-900">{t('fp.companyDataTitle')}</h2>
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  <button onClick={applyCompanyData}
                    className={`flex items-center justify-center gap-2 text-sm font-bold px-4 py-2 rounded-xl border transition-all ${companyApplied ? 'bg-green-50 text-green-700 border-green-200' : 'bg-goszakup-50 text-goszakup-700 border-sky-200 hover:bg-goszakup-100'}`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {companyApplied ? t('calc.useCompanyApplied') : t('calc.useCompanyBtn')}
                  </button>
                  <button
                    type="button"
                    aria-expanded={showPdf}
                    onClick={() => { setShowPdf(v => !v); setPdfStatus(null); }}
                    className={`flex items-center justify-center gap-2 text-sm font-bold min-h-11 px-4 py-2 rounded-xl border transition-all ${showPdf ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-goszakup-600 text-white border-goszakup-600 hover:bg-goszakup-700'}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {showPdf ? t('calc.pdfHide') : t('calc.pdfLabel')}
                  </button>
                </div>
              </div>
              {showPdf && (
                <div className="mb-4">
                  <CompanyPdfUpload inputLabel={t('calc.pdfLabel')} hint={t('calc.pdfHint')}
                    busy={isPdfUploading} onFile={handlePdfFile} />
                  {pdfStatus && (
                    <p className={`text-xs mt-1 font-medium ${pdfStatus.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>{pdfStatus.msg}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: t('calc.fieldIncome'), val: income, set: setIncome },
                  { label: t('calc.fieldTaxes'), val: taxes, set: setTaxes },
                  { label: t('calc.fieldPfot'), val: pfot, set: setPfot },
                ].map((f, i) => (
                  <div key={i}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{f.label}</label>
                    <div className="relative">
                      <input type="text" inputMode="decimal" aria-label={f.label} value={f.val} placeholder="0"
                        onChange={e => { f.set(fmtInput(e.target.value)); setCompanyApplied(false); }}
                        className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-goszakup-500 transition-all"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">₸</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* VIP Widget */}
            <div className={`rounded-xl p-5 border transition-colors ${vipMode ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${vipMode ? 'bg-white' : 'bg-amber-50'}`}>
                    <Crown className={`w-6 h-6 ${vipMode ? 'text-amber-800' : 'text-amber-700'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${vipMode ? 'bg-amber-100 text-amber-800' : 'bg-amber-100 text-amber-700'}`}>VIP</span>
                    </div>
                    <p className={`font-semibold ${vipMode ? 'text-amber-950' : 'text-gray-900'}`}>{t('calc.vipTitle')}</p>
                    <p className={`text-xs mt-0.5 ${vipMode ? 'text-amber-800' : 'text-gray-500'}`}>{t('calc.vipDesc')}</p>
                  </div>
                </div>
                <button onClick={() => { setVipMode(v => !v); setCalcResult(null); setVipResult(null); setCalcError(''); }}
                  className={`flex-shrink-0 text-xs font-bold px-4 py-2.5 rounded-xl transition-all ${vipMode ? 'border border-amber-300 bg-white text-amber-900 hover:bg-amber-100' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                >
                  {vipMode ? t('calc.vipDeactivate') : t('calc.vipActivate')}
                </button>
              </div>
            </div>

            {/* Category + Amount */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-5">
              {/* Category dropdown */}
              <div ref={categoryRef}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('calc.categoryLabel')}</label>
                {categoriesLoading ? (
                  <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                ) : (
                  <div className="relative">
                    <button onClick={() => setCategoryOpen(o => !o)} type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 border rounded-xl text-sm transition-all text-left ${categoryOpen ? 'border-goszakup-500 ring-2 ring-goszakup-500/20' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className={selectedCategory ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                        {selectedCategoryName || t('calc.categoryPlaceholder')}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${categoryOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {categoryOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                        <div className="max-h-56 overflow-y-auto">
                          {categories.map(cat => (
                            <button key={cat.key} type="button" onClick={() => { setSelectedCategory(cat.key); setCategoryOpen(false); }}
                              className={`w-full text-left px-4 py-3 text-sm hover:bg-goszakup-50 transition-colors ${selectedCategory === cat.key ? 'bg-goszakup-50 text-goszakup-700 font-bold' : 'text-gray-700'}`}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Amount + Slider (hidden in VIP) */}
              {!vipMode && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('calc.amountLabel')}</label>
                  <div className="relative mb-3">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      placeholder="0"
                      onChange={e => setAmount(fmtInput(e.target.value))}
                      onPaste={e => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text');
                        setAmount(fmtInput(pasted));
                      }}
                      className="w-full px-4 py-4 pr-10 text-2xl font-bold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 transition-all"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold">₸</span>
                  </div>
                  <input type="range" min={0} max={1000000000} step={1000000} value={parseNum(amount)}
                    onChange={e => setAmount(fmtInput(e.target.value))}
                    className="w-full h-2 cursor-pointer accent-goszakup-600 mb-1"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>0 ₸</span><span>1 000 000 000 ₸</span>
                  </div>
                </div>
              )}
            </div>

            {/* Calculate button */}
            <button onClick={() => setShowConfirm(true)} disabled={!canCalculate || isCalculating || isPdfUploading}
              className={`w-full min-h-12 py-3 px-6 rounded-lg font-semibold text-sm sm:text-base transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed ${vipMode ? 'bg-amber-700 text-white hover:bg-amber-800' : 'bg-goszakup-600 text-white hover:bg-goszakup-700 shadow-goszakup-600/20'}`}
            >
              {isCalculating
                ? <><Loader2 className="w-5 h-5 animate-spin" />{vipMode ? t('calc.vipCalculating') : t('calc.analyzing')}</>
                : vipMode
                  ? <><Crown className="w-5 h-5" />{t('calc.vipBtn')}</>
                  : t('calc.btnAnalyze')
              }
            </button>

            {/* Limit counter / warning */}
            {user !== null && (
              <div className={`flex flex-wrap items-center justify-between gap-2 text-sm px-1 ${limitsLeft === 0 ? 'text-red-700' : 'text-slate-600'}`}>
                <span>{vipMode ? t('calc.limitVip') : t('calc.limitPfu')}: <strong>{limitsLeft}</strong></span>
                {limitsLeft === 0 && (
                  <a href="/profile#billing" className="font-bold underline text-red-600 hover:text-red-700">{t('calc.limitBuyLink')}</a>
                )}
              </div>
            )}

            {/* Error */}
            {calcError && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-3 text-red-600 text-sm font-medium">
                <XCircle className="w-5 h-5 flex-shrink-0" />{calcError}
              </div>
            )}

            {/* The same result card is used by the public example. */}
            {calcResult && <PfuResultCard result={calcResult} />}

            {/* VIP result */}
            {vipResult && (
              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                <div className="px-5 py-5 border-b border-slate-200 bg-amber-50 sm:px-6">
                  <div className="flex items-center gap-3 text-amber-900">
                    <Crown className="w-6 h-6 flex-shrink-0" />
                    <div>
                      <p className="text-xl font-semibold">{t('calc.vipResultTitle')}</p>
                      <p className="text-sm opacity-80">{vipResult.category_name}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6">
                  <div className="text-center py-4 mb-4">
                    <p className="text-3xl sm:text-4xl font-semibold tabular-nums text-slate-900 tracking-tight">
                      {fmt(vipResult.max_contract_amount)} ₸
                    </p>
                    <p className="text-gray-400 mt-2 text-sm">{fmt(vipResult.max_contract_mrp)} МРП</p>
                  </div>
                  {vipResult.pun_warning && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-amber-800">{t('calc.vipWarning')}</p>
                        <p className="text-sm text-amber-700 mt-0.5">{localizedPunWarning(vipResult.pun_warning, t)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── HISTORY TAB ── */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {isHistoryLoading ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-goszakup-500" />
              </div>
            ) : historyError ? (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-red-600 text-sm">{historyError}</div>
            ) : historyItems.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-lg font-semibold text-gray-900 mb-2">{t('calc.historyEmpty')}</p>
                <p className="text-sm text-gray-400">{t('calc.historyEmptyDesc')}</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyDate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyCategory')}</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyAmount')}</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyType')}</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyStatus')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {historyItems.map(item => {
                        const passes = item.result?.passes;
                        return (
                          <tr key={item.id} onClick={() => setSelectedHistory(item)}
                            className="hover:bg-slate-50 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                              {new Date(item.created_at).toLocaleDateString('ru-RU')}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {item.result?.category_name ?? item.category_name ?? item.category_key}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {item.purchase_amount != null ? `${fmt(item.purchase_amount)} ₸` : '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.is_vip
                                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">👑 VIP</span>
                                : <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">{t('calc.tabAnalysis')}</span>
                              }
                            </td>
                            <td className="px-4 py-3 text-center">
                              {item.result?.eff_pfu != null
                                ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                    passes === true ? 'bg-green-100 text-green-700' :
                                    passes === false ? 'bg-amber-100 text-amber-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`} title={passes === true ? t('calc.historyPass') : passes === false ? t('calc.historyFail') : undefined}>
                                    {passes === true ? <CheckCircle2 className="w-3 h-3" /> : passes === false ? <Info className="w-3 h-3" /> : null}
                                    {item.result.eff_pfu}
                                  </span>
                                : <span className="text-gray-400">—</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {historyTotalPages > 1 && (
                  <Pagination
                    currentPage={historyPage}
                    totalPages={historyTotalPages}
                    onPageChange={fetchHistory}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── HISTORY DETAIL MODAL ── */}
      {selectedHistory && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSelectedHistory(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('calc.historyDetailTitle')}</h3>
              <button onClick={() => setSelectedHistory(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('calc.historyDate')}</p>
                  <p className="font-bold text-gray-900 text-sm">{new Date(selectedHistory.created_at).toLocaleString('ru-RU')}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('calc.historyType')}</p>
                  <p className="font-bold text-gray-900 text-sm">{selectedHistory.is_vip ? '👑 VIP' : t('calc.tabAnalysis')}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('calc.historyCategory')}</p>
                  <p className="font-bold text-gray-900 text-sm">{selectedHistory.result?.category_name ?? selectedHistory.category_name ?? selectedHistory.category_key}</p>
                </div>
                {selectedHistory.purchase_amount != null && (
                  <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{t('calc.historyAmount')}</p>
                    <p className="font-bold text-gray-900 text-sm">{fmt(selectedHistory.purchase_amount)} ₸</p>
                  </div>
                )}
              </div>
              {selectedHistory.result && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('calc.historyStatus')}</p>
                  {selectedHistory.result.passes !== undefined && (
                    <div className={`flex flex-wrap items-center gap-2 p-3 rounded-xl ${selectedHistory.result.passes ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>
                      {selectedHistory.result.passes ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <Info className="w-5 h-5 shrink-0" />}
                      <span className="min-w-0 flex-1 font-bold text-sm">{selectedHistory.result.passes ? t('calc.historyPass') : t('calc.historyFail')}</span>
                      {selectedHistory.result.eff_pfu !== undefined && (
                        <span className="w-full text-sm font-bold sm:w-auto sm:ml-auto">{t('calc.resultScore')}: {selectedHistory.result.eff_pfu}</span>
                      )}
                    </div>
                  )}
                  {selectedHistory.result.passes !== undefined && <p className="text-sm text-slate-600">{t('calc.resultExplanation')}</p>}
                  {selectedHistory.result.max_contract_amount !== undefined && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">{t('calc.vipResultTitle')}</p>
                      <p className="text-2xl font-semibold text-gray-900">{fmt(selectedHistory.result.max_contract_amount)} ₸</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button onClick={() => setSelectedHistory(null)}
                className="w-full py-3 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >{t('calc.historyDetailClose')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {showConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <p className="font-semibold text-gray-900 text-lg">{vipMode ? t('calc.confirmVipTitle') : t('calc.confirmTitle')}</p>
            <p className="text-sm text-gray-500">{vipMode ? t('calc.confirmVipDesc') : t('calc.confirmDesc')}</p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold hover:bg-gray-200 transition-colors"
              >{t('calc.confirmCancel')}</button>
              <button
                onClick={() => { setShowConfirm(false); calculate(); }}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors shadow-sm ${vipMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-goszakup-600 hover:bg-goszakup-700'}`}
              >{t('calc.confirmBtn')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── COMPANY MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">{t('calc.noCompanyTitle')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('calc.noCompanyDesc')}</p>
            </div>

            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* PDF Upload */}
              <div>
                <button type="button" onClick={() => { setShowPdf(v => !v); setPdfStatus(null); }}
                  className={`flex items-center gap-2 text-sm font-bold min-h-11 px-4 py-2 rounded-xl border mb-2 transition-all ${showPdf ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-goszakup-600 text-white border-goszakup-600 hover:bg-goszakup-700'}`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  {showPdf ? t('calc.pdfHide') : t('calc.pdfLabel')}
                </button>
                {showPdf && (
                  <>
                    <CompanyPdfUpload inputLabel={t('calc.pdfLabel')} hint={t('calc.pdfHint')}
                      busy={isPdfUploading} onFile={handlePdfFile} />
                    {pdfStatus && (
                      <div className={`mt-2 text-sm flex items-center gap-2 font-medium ${pdfStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {pdfStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {pdfStatus.msg}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-400">
                <div className="flex-1 h-px bg-gray-200" />
                или введите вручную
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Manual inputs */}
              {[
                { label: t('calc.fieldIncome'), val: modalIncome, set: setModalIncome },
                { label: t('calc.fieldTaxes'), val: modalTaxes, set: setModalTaxes },
                { label: t('calc.fieldPfot'), val: modalPfot, set: setModalPfot },
              ].map((f, i) => (
                <div key={i}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <div className="relative">
                    <input type="text" inputMode="decimal" aria-label={f.label} value={f.val} placeholder="0"
                      onChange={e => f.set(fmtInput(e.target.value))}
                      className="w-full px-4 pr-8 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-goszakup-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₸</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => { setShowModal(false); setPdfStatus(null); }}
                className="flex-1 py-3 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                {t('calc.noCompanyClose')}
              </button>
              <button onClick={saveModalData}
                disabled={isSavingModal || isPdfUploading || (!modalIncome && !modalTaxes && !modalPfot)}
                className="flex-1 py-3 text-sm font-bold text-white bg-goszakup-600 rounded-xl hover:bg-goszakup-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSavingModal
                  ? <><Loader2 className="w-4 h-4 animate-spin" />{t('calc.noCompanySaving')}</>
                  : t('calc.noCompanySave')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

